/**
 * Detailseite: dieses Webinar neben den letzten Vergleichswebinaren — dieselben zwei Vergleiche wie
 * die Bilder der Montagsmail (`webinarReportRender.registrationChartSvg` / `valueChartSvg`, Daten aus
 * `webinarReportData.loadWebinarComparison`). Oberste Zeile = dieses Webinar (blau, hinterlegt).
 * Die Anfo-Zahl allein vergleicht schlecht, sobald die Preise auseinanderliegen — deshalb der Wert.
 * Steht auf weissem Papier, darum feste Farben statt Theme-Grau (react-frontend.md).
 */
import { FC, ReactNode } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import type { WebinarComparisonData, WebinarComparisonRow } from '../../helpers/webinarStatisticsData';

const INK = '#0b0b0b';
const INK2 = '#52514e';
const LINE = '#e6e5e0';
const SOFT = '#f6f5f1';
const BLAU = ['#2a78d6', '#86b6ef', '#d4e4f7'];
const GRAU = ['#8a8982', '#c9c8c2', '#e6e5e0'];

const int = (v: number) => Math.round(v || 0).toLocaleString('de-DE');
const eur = (v: number) => `${int(v)} €`;
const eur2 = (v: number) =>
  `${(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const label = (w: WebinarComparisonRow) =>
  `${w.start.slice(8, 10)}.${w.start.slice(5, 7)}.${w.product ? ` ${w.product}` : ''}`;

const COLS = '96px minmax(0, 1fr) 72px 84px';

const Rows: FC<{
  rows: WebinarComparisonRow[];
  head: [string, string, string];
  parts: (w: WebinarComparisonRow) => number[];
  barText: (w: WebinarComparisonRow) => string;
  cols: [(w: WebinarComparisonRow) => ReactNode, (w: WebinarComparisonRow) => ReactNode];
}> = ({ rows, head, parts, barText, cols }) => {
  const max = Math.max(...rows.map((w) => parts(w).reduce((s, v) => s + Math.max(0, v), 0)), 1);
  const cell = (inner: ReactNode, me: boolean, align: 'left' | 'right' = 'left') => (
    <Typography
      variant="body2"
      component="div"
      sx={{ color: me ? INK : INK2, fontWeight: me ? 700 : 400, textAlign: align, whiteSpace: 'nowrap' }}
    >
      {inner}
    </Typography>
  );
  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: COLS,
          columnGap: 1.5,
          px: 1,
          pb: 0.5,
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <span />
        <Typography variant="caption" sx={{ color: INK2 }}>
          {head[0]}
        </Typography>
        <Typography variant="caption" sx={{ color: INK2, textAlign: 'right' }}>
          {head[1]}
        </Typography>
        <Typography variant="caption" sx={{ color: INK2, textAlign: 'right' }}>
          {head[2]}
        </Typography>
      </Box>
      {rows.map((w, i) => {
        const me = i === 0;
        const farbe = me ? BLAU : GRAU;
        return (
          <Box
            key={w.webinarId}
            sx={{
              display: 'grid',
              gridTemplateColumns: COLS,
              columnGap: 1.5,
              alignItems: 'center',
              px: 1,
              py: 0.75,
              borderBottom: `1px solid ${LINE}`,
              backgroundColor: me ? SOFT : 'transparent',
            }}
          >
            <Tooltip title={w.title} arrow placement="top">
              <Box>{cell(label(w), me)}</Box>
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', flex: '0 1 auto', width: '78%', height: 12 }}>
                {parts(w).map((v, k) =>
                  v > 0 ? (
                    <Box
                      key={k}
                      sx={{ width: `${(100 * v) / max}%`, height: 12, backgroundColor: farbe[k] }}
                    />
                  ) : null,
                )}
              </Box>
              {cell(barText(w), me)}
            </Box>
            {cell(cols[0](w), me, 'right')}
            {cell(cols[1](w), me, 'right')}
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Opt-in `sektion` (nur Sektions-Detailseite, Persona-Check 26.10.2026 B18/B20/B22): Überschriften „dieser Termin und
 * die N davor", Begriffe der Sektion („während des Webinars eingestiegen", „Erwarteter Wert"), kein CPA-Satz, und
 * `ohneTeilnahme` (webinar_id → Grund) zeigt bei Terminen ohne belastbare Teilnahme „—" statt des Teilnehmer-Teils —
 * dieselbe Entscheidung wie die Kapitelseite (`showUpZeile`). Ohne den Schalter (alte Seite) unverändert.
 */
const WebinarComparison: FC<{
  data: WebinarComparisonData;
  geldbasis: 'brutto' | 'netto';
  sektion?: boolean;
  ohneTeilnahme?: Record<string, string>;
}> = ({ data, geldbasis, sektion = false, ohneTeilnahme }) => {
  const rows = data.rows;
  const grund = (w: WebinarComparisonRow): string | null => (sektion ? (ohneTeilnahme?.[w.webinarId] ?? null) : null);
  if (rows.length < 2) return null;
  const n = rows.length - 1;
  const legende = (farben: string[], texte: string[]) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
      {texte.map((t, k) => (
        <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: farben[k] }} />
          <Typography variant="caption" sx={{ color: INK2 }}>
            {t}
          </Typography>
        </Box>
      ))}
    </Box>
  );
  const offen = rows.some((w) => w.ordersWithoutCpa > 0);
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: INK }}>
        {sektion ? `Anmeldungen: dieser Termin und die ${n} davor` : `Anmeldungen: die letzten ${n} Webinare`}
      </Typography>
      <Typography variant="body2" sx={{ color: INK2, mb: 1.5 }}>
        Oberste Zeile (blau): dieses Webinar. Rechts rechtzeitig Angemeldete und Teilnehmer.
      </Typography>
      <Rows
        rows={rows}
        head={['Anmeldungen', 'Rechtzeitig', 'Teilnehmer']}
        parts={(w) => (grund(w) ? [0, w.timely, w.registrations - w.timely] : [w.attendees, w.timely - w.attendees, w.registrations - w.timely])}
        barText={(w) => int(w.registrations)}
        cols={[
          (w) => int(w.timely),
          (w) => {
            const g = grund(w);
            return g ? (
              <Tooltip title={g} arrow placement="top">
                <Box component="span" sx={{ cursor: 'help' }}>
                  —
                </Box>
              </Tooltip>
            ) : (
              int(w.attendees)
            );
          },
        ]}
      />
      {legende(GRAU, [
        'Teilnehmer',
        'rechtzeitig angemeldet, nicht dabei',
        sektion ? 'während des Webinars eingestiegen' : 'nach Sendestart angemeldet',
      ])}
      {sektion && rows.some((w) => grund(w)) && (
        <Typography variant="caption" sx={{ color: INK2, display: 'block', mt: 0.5 }}>
          — = Teilnahme dieses Termins nicht belastbar (Grund im Tooltip); der Balken zeigt dort nur die Anmeldungen.
        </Typography>
      )}

      <Typography variant="h6" sx={{ fontWeight: 700, color: INK, mt: 4 }}>
        {sektion
          ? `Erwarteter Wert: dieser Termin und die ${n} davor`
          : `${geldbasis === 'netto' ? 'Wert bei Bestellung' : 'Anfo-Wert'}: die letzten ${n} Webinare`}
      </Typography>
      <Typography variant="body2" sx={{ color: INK2, mb: 1.5 }}>
        {sektion ? (
          <>
            Erwarteter Wert aller Anfos (netto): je Anfo der Wert ihres Angebots, sonst des Produkts – derselbe Maßstab für
            alte und junge Webinare, und vergleichbar auch bei sehr verschiedenen Preisen, die Anfo-Zahl allein ist es nicht.
            Oberste Zeile: dieses Webinar, Stand heute.
          </>
        ) : geldbasis === 'netto' ? (
          <>
            Wert bei Bestellung aller Anfos (netto): je Anfo der Wert ihres Angebots, sonst des Produkts – derselbe
            Maßstab für alte und junge Webinare, und vergleichbar auch bei sehr verschiedenen Preisen, die Anfo-Zahl
            allein ist es nicht. Der CPA-Wert ist ein vereinbarter Anteil davon. Oberste Zeile: dieses Webinar, Stand
            heute.
          </>
        ) : (
          <>
            Anfo-Wert aller Anfos ({geldbasis}) – vergleichbar auch bei sehr verschiedenen Preisen, die Anfo-Zahl
            allein ist es nicht. Der CPA-Wert ist ein vereinbarter Anteil davon. Ältere Webinare: echte Zahlungen
            hochgerechnet, nie gezahlte Anfos zählen 0 €; jüngere: überwiegend der erwartete Wert des Angebots.
            Oberste Zeile: dieses Webinar, Stand heute.
          </>
        )}
      </Typography>
      <Rows
        rows={rows}
        head={[sektion ? 'Erwarteter Wert' : geldbasis === 'netto' ? 'Wert bei Bestellung' : 'Anfo-Wert', 'je Anfo', 'je rechtzeitig Angemeldeten']}
        parts={(w) => [w.ltvRevenue]}
        barText={(w) => `${eur(w.ltvRevenue)}${w.ordersWithoutCpa > 0 ? '*' : ''}`}
        cols={[
          (w) => {
            const bewertet = Math.max(0, w.orders - w.ordersWithoutCpa);
            return bewertet > 0 ? eur(w.ltvRevenue / bewertet) : '–';
          },
          (w) => (w.timely > 0 ? eur2(w.ltvRevenue / w.timely) : '–'),
        ]}
      />
      {offen && (
        <Typography variant="caption" sx={{ color: INK2, display: 'block', mt: 1 }}>
          * Einzelne Anfos noch ohne Wert, die Summe ist dort etwas zu niedrig.
        </Typography>
      )}
    </Box>
  );
};

export default WebinarComparison;
